import * as React from 'react';
import config from '../config/config'; // Import der Konfigurationsdatei
import { useNavigate } from 'react-router-dom';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import { process } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Loader } from '@progress/kendo-react-indicators';
import { pencilIcon, trashIcon } from '@progress/kendo-svg-icons';

const DATA_ITEM_KEY = 'Customer-ID';

const Customers = () => {
    const [data, setData] = React.useState([]);
    const [dataState, setDataState] = React.useState({ take: 100, skip: 0 });
    const [dataResult, setDataResult] = React.useState([]);
    const [visibleDialog, setVisibleDialog] = React.useState(false);
    const [selectedCustomer, setSelectedCustomer] = React.useState(null);
    const [searchText, setSearchText] = React.useState('');
    const [notifications, setNotifications] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const navigate = useNavigate();

    const showNotification = (message, type) => {
        const newNotification = {
            message,
            type,
            id: new Date().getTime(),
        };
        setNotifications((prev) => [...prev, newNotification]);
        setTimeout(() => {
            setNotifications((prev) => prev.filter((n) => n.id !== newNotification.id));
        }, 5000);
    };

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${config.host}/customers`);
                const customers = await response.json();
                const formattedData = customers.map((customer) => ({
                    ...customer,
                    address: `${customer['Strasse']} ${customer['Hausnummer']}, ${customer['PLZ']} ${customer['ORT']}`
                }));
                setData(formattedData);
                setDataResult(process(formattedData, dataState));
            } catch (err) {
                showNotification('Fehler beim Laden der Kundendaten.', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dataState]);

    const handleEdit = (customer) => {
        navigate(`/edit-customers/${customer['Customer-ID']}`);
    };

    const handleDelete = (customer) => {
        setSelectedCustomer(customer);
        setVisibleDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            const response = await fetch(`${config.host}/customers/${selectedCustomer['Customer-ID']}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                setData(data.filter((item) => item['Customer-ID'] !== selectedCustomer['Customer-ID']));
                setDataResult(process(data.filter((item) => item['Customer-ID'] !== selectedCustomer['Customer-ID']), dataState));
                showNotification('Kunde erfolgreich gelöscht.', 'success');
            } else {
                showNotification('Fehler beim Löschen des Kunden.', 'error');
            }
        } catch (err) {
            showNotification('Fehler beim Löschen des Kunden.', 'error');
        } finally {
            setVisibleDialog(false);
            setSelectedCustomer(null);
        }
    };

    const handleCancelDelete = () => {
        setVisibleDialog(false);
        setSelectedCustomer(null);
    };

    const dataStateChange = (event) => {
        setDataState(event.dataState);
        setDataResult(process(data, event.dataState));
    };

    const handleSearch = (event) => {
        const value = event.target.value.toLowerCase();
        setSearchText(value);
        const filteredData = data.filter((customer) =>
            customer['Vorname'].toLowerCase().includes(value) ||
            customer['Nachname'].toLowerCase().includes(value) ||
            customer['EmailAdresse'].toLowerCase().includes(value) ||
            customer['Firma'].toLowerCase().includes(value)
        );
        setDataResult(process(filteredData, dataState));
    };

    return (
        <div id="Customers" className="profile-page main-content">
            <div className="card-container">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 className="card-title">Kundenverwaltung</h3>
                    <Button themeColor="primary" onClick={() => navigate('/create-customers')}>
                        Kunde hinzufügen
                    </Button>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', marginTop: '100px' }}>
                        <Loader size="large" type="infinite-spinner" />
                        <p>Kundendaten werden geladen...</p>
                    </div>
                ) : (
                    <Grid
                        style={{ height: '100%' }}
                        data={dataResult}
                        pageable={{ 
                            pageSizes: true,
                            pageSizes: [50, 100, 150]
                        }}
                        sortable
                        {...dataState}
                        onDataStateChange={dataStateChange}
                        dataItemKey={DATA_ITEM_KEY}
                        resizable
                    >
                        <GridToolbar>
                            <Input
                                value={searchText}
                                onChange={handleSearch}
                                style={{
                                    border: '2px solid #ccc',
                                    boxShadow: 'inset 0px 0px 0.5px 0px rgba(0,0,0,0.0.1)',
                                    width: '170px',
                                    height: '30px',
                                    marginRight: '10px',
                                }}
                                placeholder="In allen Spalten suchen..."
                            />
                        </GridToolbar>

                        <Column field="Firma" title="Firma" width="200px" />
                        <Column field="Vorname" title="Vorname" width="150px" />
                        <Column field="Nachname" title="Nachname" width="150px" />
                        <Column field="EmailAdresse" title="E-Mail Adresse" />
                        <Column field="Telefonnummer" title="Telefonnummer" />
                        <Column field="address" title="Adresse" />

                        <Column
                            title="Aktionen"
                            width="110px"
                            cell={(props) => (
                                <td>
                                    <Button
                                        icon="edit"
                                        svgIcon={pencilIcon}
                                        fillMode="flat"
                                        onClick={() => handleEdit(props.dataItem)}
                                    />


                                    {/* --- Vorrübergehend deaktiviert! --- 

                                    <Button
                                        icon="delete"
                                        svgIcon={trashIcon}
                                        fillMode="flat"
                                        onClick={() => handleDelete(props.dataItem)}
                                    />
                                    */}
                                </td>
                            )}
                        />
                    </Grid>
                )}
            </div>

            {visibleDialog && (
                <Dialog title="Löschen bestätigen" onClose={handleCancelDelete}>
                    <p style={{ marginTop: '20px', marginBottom: '110px', textAlign: 'center' }}>
                        Möchten Sie {selectedCustomer?.Firma} wirklich löschen?
                    </p>
                    <DialogActionsBar>
                        <Button themeColor="primary" onClick={handleConfirmDelete}>Ja</Button>
                        <Button onClick={handleCancelDelete}>Abbrechen</Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
};

export default Customers;
